import { SegmentedControl, Stack } from '@mantine/core'
import { classRunningCount, findCar, runningCount } from '@racedash/shared'
import { Datum, NoValue, Tile } from '../Tile'
import type { WidgetEditorProps, WidgetProps } from './props'

/**
 * Overall position, or position in class.
 *
 * Class position is derived by the interpreter because neither vendor supplies
 * it, and most club championships are scored on it: a car twelfth on the road
 * and leading its class is having a good day, and a tile that only says "12"
 * does not show that.
 */
export default function Position({ config, car, data }: WidgetProps<'position'>) {
  const standing = findCar(data.standings, car)
  const inClass = config.of === 'class'

  const value = inClass ? standing?.classPosition : standing?.position
  const field = inClass
    ? classRunningCount(data.standings, standing?.class)
    : runningCount(data.standings)

  // Zero means "not running" for overall and "no class" for class position.
  // Neither is a position, and rendering it as P0 would be a claim.
  if (!value || value <= 0) {
    return (
      <Tile label={inClass ? 'In class' : 'Position'}>
        <NoValue />
      </Tile>
    )
  }

  return (
    <Tile label={inClass ? classLabel(standing?.class) : 'Position'}>
      <Datum value={value} sub={field > 0 ? `of ${field}` : undefined} />
    </Tile>
  )
}

/**
 * "In class" alone loses which class, and the bare class name alone does not
 * say it is a position — sitting next to an overall-position tile showing the
 * same figure, that reads as a duplicate.
 */
function classLabel(name: string | undefined): string {
  return name ? `In class · ${name}` : 'In class'
}

/** Overall or in class. Nothing else about this widget is worth a setting. */
export function Editor({ config, onChange }: WidgetEditorProps<'position'>) {
  return (
    <Stack gap="xs">
      <SegmentedControl
        fullWidth
        value={config.of}
        onChange={(v) => onChange({ kind: 'position', of: v as 'overall' | 'class' })}
        data={[
          { value: 'overall', label: 'Overall' },
          { value: 'class', label: 'In class' },
        ]}
      />
    </Stack>
  )
}
